// Women's Hormone Balance quiz — ORIGINAL wording, strictly non-diagnostic. 15
// questions across 4 dimensions. Tally scoring → 4 support "themes". Carries an
// extra `disclaimer` (pregnancy / meds / see a provider) — the most sensitive topic.
const hormones = {
  slug: "hormones",
  title: "What kind of hormone support do you need?",
  emoji: "🌸",
  blurb: "A 15-question check across 4 dimensions to find your hormone-balance focus and matching herbs.",
  seoTitle: "Women's Hormone Balance Quiz | Sarv",
  seoDescription:
    "Take the free women's hormone quiz to find your focus — cycle, PMS and mood, energy, or perimenopause — and the herbs traditionally used to support each.",
  keywords:
    "hormone balance quiz, women's hormone quiz, PMS remedies, perimenopause herbs, natural hormone support, herbs for hormonal balance",
  intro: "15 questions across your cycle, mood, physical signs, and energy. General wellness only — not a medical diagnosis.",
  evidenceNote:
    "This assessment is organized around cycle, mood/PMS, physical signs, and energy/life-stage. The herbs it points toward have clinical research behind their traditional use — chasteberry (vitex) has systematic-review support for PMS, and black cohosh has mixed but promising evidence for menopausal hot flashes (see the balanced NCCIH reviews below). It is an educational self-reflection tool and cannot diagnose any hormonal condition.",
  references: [
    {
      label: "Chasteberry: Usefulness and Safety",
      publisher: "NCCIH / NIH",
      url: "https://www.nccih.nih.gov/health/chasteberry",
    },
    {
      label: "Vitex agnus castus for PMS and premenstrual dysphoric disorder: a systematic review",
      publisher: "PubMed / NIH",
      url: "https://pubmed.ncbi.nlm.nih.gov/29063202/",
    },
    {
      label: "Vitex agnus-castus for premenstrual, postmenstrual and infertility disorders: a systematic review",
      publisher: "PMC / NIH",
      url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC5308513/",
    },
    {
      label: "Menopausal Symptoms: In Depth",
      publisher: "NCCIH / NIH",
      url: "https://www.nccih.nih.gov/health/menopausal-symptoms-in-depth",
    },
    {
      label: "Black Cohosh: Usefulness and Safety",
      publisher: "NCCIH / NIH",
      url: "https://www.nccih.nih.gov/health/black-cohosh",
    },
    {
      label: "Herbal products used in menopause and for gynecological disorders",
      publisher: "PMC / NIH",
      url: "https://www.ncbi.nlm.nih.gov/pmc/articles/PMC8708702/",
    },
  ],
  disclaimer:
    "This quiz is for general education only and cannot diagnose any hormonal or medical condition. Hormonal symptoms can have many causes — please consult a qualified healthcare provider for persistent or concerning symptoms, and before using any herb if you are pregnant, nursing, trying to conceive, or taking medication (including hormonal birth control).",
  scoring: { mode: "tally" },
  questions: [
    // --- Your cycle ---
    {
      section: "Your cycle",
      question: "Which best describes your cycle lately?",
      options: [
        { label: "Irregular, unpredictable, or missed periods", scores: { cycle: 1 } },
        { label: "Regular, but with rough PMS beforehand", scores: { pms: 1 } },
        { label: "Regular, but I'm wiped out and low-energy", scores: { energy: 1 } },
        { label: "Changing or spacing out, with new symptoms (I'm 40+)", scores: { peri: 1 } },
      ],
    },
    {
      section: "Your cycle",
      question: "How would you describe your periods (if you have them)?",
      options: [
        { label: "Unpredictable, skipped, or very irregular", scores: { cycle: 1 } },
        { label: "Regular, but heralded by clear PMS", scores: { pms: 1 } },
        { label: "Regular, but they leave me exhausted", scores: { energy: 1 } },
        { label: "Getting lighter, heavier, or spacing out with age", scores: { peri: 1 } },
      ],
    },
    {
      section: "Your cycle",
      question: "How predictable is your cycle?",
      options: [
        { label: "Unpredictable or irregular", scores: { cycle: 1 } },
        { label: "Predictable — PMS announces it", scores: { pms: 1 } },
        { label: "Predictable, but exhausting", scores: { energy: 1 } },
        { label: "Becoming less predictable with age", scores: { peri: 1 } },
      ],
    },
    {
      section: "Your cycle",
      question: "What's your main cycle-related concern?",
      options: [
        { label: "Irregular timing", scores: { cycle: 1 } },
        { label: "Cramps and PMS", scores: { pms: 1 } },
        { label: "Low energy overshadows everything", scores: { energy: 1 } },
        { label: "New symptoms I didn't used to have", scores: { peri: 1 } },
      ],
    },
    // --- Mood & PMS ---
    {
      section: "Mood & PMS",
      question: "How's your mood through the month?",
      options: [
        { label: "Hard to predict along with my cycle", scores: { cycle: 1 } },
        { label: "Dips sharply in the week or two before my period", scores: { pms: 1 } },
        { label: "Persistently low or unmotivated", scores: { energy: 1 } },
        { label: "More anxious or up-and-down than it used to be", scores: { peri: 1 } },
      ],
    },
    {
      section: "Mood & PMS",
      question: "What do you notice most?",
      options: [
        { label: "Cycle irregularity or spotting", scores: { cycle: 1 } },
        { label: "Mood swings, irritability, or tender breasts before my period", scores: { pms: 1 } },
        { label: "Fatigue, low libido, or feeling 'flat'", scores: { energy: 1 } },
        { label: "Hot flashes, night sweats, or new sleep changes", scores: { peri: 1 } },
      ],
    },
    {
      section: "Mood & PMS",
      question: "How's your emotional wellbeing lately?",
      options: [
        { label: "It rises and falls with my cycle", scores: { cycle: 1 } },
        { label: "It dips hard in the premenstrual week", scores: { pms: 1 } },
        { label: "Persistently flat or low", scores: { energy: 1 } },
        { label: "More anxious or irritable than before (I'm 40+)", scores: { peri: 1 } },
      ],
    },
    {
      section: "Mood & PMS",
      question: "Which would you most like support with?",
      options: [
        { label: "A more regular, predictable cycle", scores: { cycle: 1 } },
        { label: "Smoother PMS and steadier mood", scores: { pms: 1 } },
        { label: "More energy and drive", scores: { energy: 1 } },
        { label: "Easing perimenopause changes", scores: { peri: 1 } },
      ],
    },
    // --- Physical signs ---
    {
      section: "Physical signs",
      question: "Which physical signs do you notice?",
      options: [
        { label: "Spotting, cycle-linked acne, or unpredictable timing", scores: { cycle: 1 } },
        { label: "Bloating, breast tenderness, or cravings before my period", scores: { pms: 1 } },
        { label: "Low libido, hair or skin changes, feeling depleted", scores: { energy: 1 } },
        { label: "Hot flashes, night sweats, or new aches", scores: { peri: 1 } },
      ],
    },
    {
      section: "Physical signs",
      question: "How's your skin, hair, and body lately?",
      options: [
        { label: "Cycle-linked breakouts or changes", scores: { cycle: 1 } },
        { label: "Bloating and tenderness before my period", scores: { pms: 1 } },
        { label: "Hair thinning, dryness, or low vitality", scores: { energy: 1 } },
        { label: "Hot flashes, night sweats, or aches", scores: { peri: 1 } },
      ],
    },
    {
      section: "Physical signs",
      question: "How's your sleep in relation to your cycle or stage?",
      options: [
        { label: "It varies unpredictably", scores: { cycle: 1 } },
        { label: "Worse in the days before my period", scores: { pms: 1 } },
        { label: "Consistently poor and unrefreshing", scores: { energy: 1 } },
        { label: "Disrupted by night sweats or waking (I'm 40+)", scores: { peri: 1 } },
      ],
    },
    {
      section: "Physical signs",
      question: "How's your libido and drive?",
      options: [
        { label: "It fluctuates with my cycle", scores: { cycle: 1 } },
        { label: "It dips before my period", scores: { pms: 1 } },
        { label: "Persistently low", scores: { energy: 1 } },
        { label: "Changed noticeably in the last year or two", scores: { peri: 1 } },
      ],
    },
    // --- Energy & life stage ---
    {
      section: "Energy & life stage",
      question: "What's your energy and drive like?",
      options: [
        { label: "It fluctuates unpredictably with my cycle", scores: { cycle: 1 } },
        { label: "It crashes in the premenstrual week", scores: { pms: 1 } },
        { label: "Persistently low — I'm running on empty", scores: { energy: 1 } },
        { label: "Changed noticeably in the last year or two", scores: { peri: 1 } },
      ],
    },
    {
      section: "Energy & life stage",
      question: "What's your life stage?",
      options: [
        { label: "My cycles have always been a bit irregular", scores: { cycle: 1 } },
        { label: "Regular cycles with monthly PMS", scores: { pms: 1 } },
        { label: "Busy, stressed, and running low", scores: { energy: 1 } },
        { label: "In my 40s+ and noticing a shift", scores: { peri: 1 } },
      ],
    },
    {
      section: "Energy & life stage",
      question: "Which sentence sounds most like you?",
      options: [
        { label: "My cycle is hard to predict", scores: { cycle: 1 } },
        { label: "I dread the week before my period", scores: { pms: 1 } },
        { label: "I feel flat, tired, and low on drive", scores: { energy: 1 } },
        { label: "My body feels like it's changing (I'm 40+)", scores: { peri: 1 } },
      ],
    },
  ],
  results: {
    cycle: {
      name: "Cycle Support",
      emoji: "🌙",
      tagline: "Your focus: a more regular, predictable cycle.",
      summary:
        "Your answers point toward cycle regularity as your main focus — irregular, unpredictable, or hard-to-track periods. It's worth knowing that the menstrual cycle is remarkably sensitive to the rest of your life: blood-sugar swings, under-eating, over-exercising, stress, and disrupted sleep can all nudge it off rhythm, which is why the foundations matter as much as any single remedy. Traditionally, certain herbs (such as chaste tree/vitex) are used to support a more balanced, regular cycle, best alongside steady nutrition, sleep, and stress care. Tracking your cycle over a few months is genuinely useful — both to spot your own patterns and to give a healthcare provider something concrete to work with if irregularity persists, since it can occasionally signal something worth investigating.",
      focus: [
        "Steady blood sugar, sleep, and stress — all shape the cycle.",
        "Herbs traditionally used to support cycle regularity.",
        "Track your cycle to spot patterns worth discussing with a provider.",
      ],
      herbs: ["Chaste tree (vitex)", "Red raspberry leaf", "Cinnamon"],
      categorySlug: "womens-health-and-hormonal-support",
    },
    pms: {
      name: "PMS & Mood",
      emoji: "🌸",
      tagline: "Your focus: smoother PMS and steadier mood.",
      summary:
        "Your answers point toward premenstrual symptoms and mood as your focus — the tension, irritability, and physical changes that cluster in the days before your period. These shifts track the natural hormonal changes of the luteal phase, and they're often amplified by low magnesium, poor sleep, and high stress, which is why lifestyle and herbs tend to work best together. Chaste tree (vitex) in particular has systematic-review support for easing PMS, and it's traditionally paired with magnesium-rich foods, movement, and rest, plus easing back on caffeine and alcohol in that pre-period window. If your symptoms are severe enough to disrupt your life each month, it's worth discussing with a provider — more significant PMS or PMDD has specific, effective options worth exploring.",
      focus: [
        "Support the luteal phase with sleep, movement, and magnesium-rich foods.",
        "Herbs traditionally used to ease PMS tension and mood swings.",
        "Reduce caffeine and alcohol in the week before your period.",
      ],
      herbs: ["Chaste tree (vitex)", "Cramp bark", "Lemon balm"],
      categorySlug: "womens-health-and-hormonal-support",
    },
    energy: {
      name: "Energy & Vitality",
      emoji: "✨",
      tagline: "Your focus: rebuilding energy and drive.",
      summary:
        "Your answers point toward low energy and vitality as your focus — feeling flat, depleted, or low on drive. Energy is the end result of many systems working together, so persistent fatigue is often less about hormones alone and more about the whole picture: sleep quality, protein and overall nutrition, stress, and recovery. Traditionally, nourishing adaptogenic herbs are used to help rebuild reserves and steady energy over time, but they work best as part of a foundation of genuine rest, adequate protein, and realistic pacing rather than as a standalone fix. Because ongoing fatigue can also stem from things like thyroid issues, low iron, or other medical causes, it's genuinely worth ruling those out with your provider alongside any natural approach — so you're treating the real cause, not just the tiredness.",
      focus: [
        "Prioritize recovery: sleep, protein, and realistic pacing.",
        "Adaptogenic, nourishing herbs traditionally used to restore vitality.",
        "Rule out other causes of fatigue with your provider.",
      ],
      herbs: ["Ashwagandha", "Maca", "Shatavari"],
      categorySlug: "womens-health-and-hormonal-support",
    },
    peri: {
      name: "Perimenopause Support",
      emoji: "🍃",
      tagline: "Your focus: easing the perimenopause transition.",
      summary:
        "Your answers point toward perimenopause-related changes — the shifting cycles, hot flashes, sleep disruption, and mood changes that mark the transition toward menopause. This is a natural life stage rather than a problem to be fixed, though the symptoms can be genuinely disruptive, and the fluctuating (not simply declining) hormones behind them explain why things can feel so unpredictable. Some herbs, such as black cohosh, have mixed but promising evidence for easing common transition symptoms like hot flashes, and they're traditionally paired with cooling routines and strong sleep support. Because this stage also affects longer-term health — bones, heart, and mood — it's a good one to navigate in partnership with a provider who can look at the bigger picture, not just the day-to-day symptoms.",
      focus: [
        "Support sleep and cooling routines for night sweats and hot flashes.",
        "Herbs traditionally used to ease perimenopause symptoms.",
        "Partner with a provider on the bigger picture of this transition.",
      ],
      herbs: ["Black cohosh", "Sage", "Red clover"],
      categorySlug: "womens-health-and-hormonal-support",
    },
  },
};

export default hormones;
